import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const readSession = (key) => {
  try {
    const s = JSON.parse(localStorage.getItem(key));
    return s && new Date(s.expires_at) > new Date() ? s : null;
  } catch {
    return null;
  }
};

const errorMessage = (err) => {
  const detail = err.response?.data?.detail;
  return typeof detail === "string" ? detail : "Não foi possível verificar agora. Tente novamente.";
};

const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// Password gate backed by a server-issued 24h token; protected data only comes from `resourceUrl`.
export const useGatedResource = ({ storageKey, authUrl, logoutUrl, resourceUrl }) => {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async (token) => {
      try {
        const res = await axios.get(resourceUrl, authHeader(token));
        setData(res.data);
      } catch (err) {
        localStorage.removeItem(storageKey);
        if (err.response?.status !== 401) setError(errorMessage(err));
      }
    },
    [resourceUrl, storageKey]
  );

  useEffect(() => {
    const session = readSession(storageKey);
    if (!session) localStorage.removeItem(storageKey);
    (session ? load(session.token) : Promise.resolve()).finally(() => setChecking(false));
  }, [load, storageKey]);

  const login = async () => {
    if (!password) {
      setError("Digite a senha de acesso.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(authUrl, { password });
      localStorage.setItem(storageKey, JSON.stringify(res.data));
      setPassword("");
      await load(res.data.token);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    const session = readSession(storageKey);
    if (session) await axios.post(logoutUrl, null, authHeader(session.token)).catch(() => null);
    localStorage.removeItem(storageKey);
    setData(null);
    setError("");
  };

  const authRequest = (config) =>
    axios({ ...config, ...authHeader(readSession(storageKey)?.token) });

  return { data, setData, checking, password, setPassword, error, submitting, login, logout, authRequest };
};
